/** Running (DESIGN §19.4): metres by type, and each run's bests. The twin of `RunMetres` in WeightsCore. */
import { addDays, mondayOf } from "./days.js";
import { norm } from "./plan.js";
import { isDistance, isInParts, kilograms, speedNumber, weightNumber } from "./sets.js";
import { shownUnit, shownWeight } from "../prefs.js";
import type { Block, HistoryWorkout, Session, SetLog } from "./types.js";

export type RunType = "sprint" | "accel" | "tempo" | "resisted" | "hills" | "drills";

export const RUN_TYPES: RunType[] = ["sprint", "accel", "tempo", "resisted", "hills", "drills"];

export const RUN_TYPE_LABEL: Record<RunType, string> = {
  sprint: "Sprint",
  accel: "Accel",
  tempo: "Tempo",
  resisted: "Resisted",
  hills: "Hills",
  drills: "Drills",
};

/** The type of a run by its name: the first of these the name says. */
export function runType(name: string): RunType {
  const words = new Set(String(name).toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean));
  const says = (...any: string[]) => any.some((w) => words.has(w));
  if (says("resisted", "sled", "1080")) return "resisted";
  if (says("uphill", "hill", "hills")) return "hills";
  if (says("tempo")) return "tempo";
  if (says("accel", "accels", "acceleration")) return "accel";
  if (says("skip", "skips", "bound", "bounds", "bounding", "drill", "drills")) return "drills";
  return "sprint";
}

export type RunMetres = Partial<Record<RunType, number>>;

export function metresTotal(m: RunMetres): number {
  return RUN_TYPES.reduce((n, t) => n + (m[t] ?? 0), 0);
}

/** Types with metres, most first. */
export function metresOrdered(m: RunMetres): { type: RunType; meters: number }[] {
  return RUN_TYPES.filter((t) => (m[t] ?? 0) > 0)
    .map((type) => ({ type, meters: m[type]! }))
    .sort((a, b) => b.meters - a.meters);
}

/** A week's bar, bottom up, in the order the app stacks it. */
export function metresStack(m: RunMetres): { type: RunType; from: number; meters: number }[] {
  let from = 0;
  return RUN_TYPES.filter((t) => (m[t] ?? 0) > 0).map((type) => {
    const seg = { type, from, meters: m[type]! };
    from += seg.meters;
    return seg;
  });
}

export function addMetres(a: RunMetres, b: RunMetres): RunMetres {
  const out: RunMetres = { ...a };
  for (const t of RUN_TYPES) if (b[t]) out[t] = (out[t] ?? 0) + b[t]!;
  return out;
}

function exerciseNames(blocks: Block[] | null | undefined): Map<string, string> {
  const names = new Map<string, string>();
  for (const b of blocks ?? []) for (const e of b.exercises ?? []) if (!names.has(norm(e.id))) names.set(norm(e.id), e.name);
  return names;
}

/** A workout's done distance reps. */
export function sessionMetres(session: Pick<Session, "blocks" | "logs"> | null | undefined): RunMetres {
  const names = exerciseNames(session?.blocks);
  const out: RunMetres = {};
  for (const log of session?.logs ?? []) {
    if (!isDistance(log)) continue;
    const meters = log.amount;
    if (meters == null || !(meters > 0)) continue;
    const type = runType(names.get(norm(log.exerciseID)) ?? log.exerciseKey ?? "");
    out[type] = (out[type] ?? 0) + meters;
  }
  return out;
}

/** One of the day's other sessions is a run when its lines give a distance (`\d m`). */
export function isRunning(lines: string[] | null | undefined): boolean {
  return (lines ?? []).some((l) => /\d\s*m\b/.test(String(l)));
}

/** `1,600 m`. */
export function metresText(meters: number): string {
  return Math.round(meters).toLocaleString("en-US") + " m";
}

/** "Tempo 800 m · Sprint 400 m". */
export function metresByTypeText(m: RunMetres): string {
  return metresOrdered(m)
    .map((x) => RUN_TYPE_LABEL[x.type] + " " + metresText(x.meters))
    .join(" · ");
}

export interface WeekMetres {
  week: string;
  byType: RunMetres;
  total: number;
}

/** Each Monday's week of done distance reps. */
export function weeklyMetres(workouts: (HistoryWorkout | { date: string; session?: Session | null })[], mondays: string[]): WeekMetres[] {
  const by = new Map<string, RunMetres>(mondays.map((m) => [m, {}]));
  for (const w of workouts) {
    const week = mondayOf(w.date);
    const now = by.get(week);
    if (now) by.set(week, addMetres(now, sessionMetres(w.session)));
  }
  return mondays.map((week) => {
    const byType = by.get(week)!;
    return { week, byType, total: metresTotal(byType) };
  });
}

/** The twelve Mondays up to `today`'s week, oldest first. */
export function lastMondays(today: string, n = 12): string[] {
  const now = mondayOf(today);
  return Array.from({ length: n }, (_, i) => addDays(now, (i - n + 1) * 7));
}

export function hasRuns(workouts: { session?: Pick<Session, "logs"> | null }[]): boolean {
  return workouts.some((w) => (w.session?.logs ?? []).some((l) => isDistance(l) && (l.amount ?? 0) > 0));
}

/** A rep's load, in kilograms to a tenth; null for a free run. */
export function runLoad(log: SetLog): number | null {
  const kg = kilograms(log);
  return kg != null && kg > 0 ? Math.round(kg * 10) / 10 : null;
}

export interface RunBest {
  name: string;
  meters: number;
  kilograms: number | null;
  fastest: { time: number; date: string } | null;
  topSpeed: { speed: number; date: string } | null;
}

function exercisesInParts(blocks: Block[] | null | undefined): Set<string> {
  const ids = new Set<string>();
  for (const b of blocks ?? []) for (const e of b.exercises ?? []) if (isInParts({}, e)) ids.add(norm(e.id));
  return ids;
}

/** A row per run (exercise, distance, load): the fastest timed rep and the highest top speed, and
 * when. A rep in parts sets neither: 250 m of two runs and a rest is not a 250 m (§19.2). */
export function runBests(workouts: { date: string; session?: Pick<Session, "blocks" | "logs"> | null }[]): RunBest[] {
  const rows = new Map<string, RunBest & { last: string }>();
  for (const w of workouts) {
    const names = exerciseNames(w.session?.blocks);
    const inParts = exercisesInParts(w.session?.blocks);
    for (const log of w.session?.logs ?? []) {
      if (!isDistance(log) || log.amount == null || !(log.amount > 0)) continue;
      if (inParts.has(norm(log.exerciseID)) || isInParts(log)) continue;
      const time = log.time != null && log.time > 0 ? log.time : null;
      const speed = log.topSpeed != null && log.topSpeed > 0 ? log.topSpeed : null;
      if (time == null && speed == null) continue;
      const name = (names.get(norm(log.exerciseID)) ?? log.exerciseKey ?? "Run").trim();
      const kg = runLoad(log);
      const key = name.toLowerCase() + "|" + log.amount + "|" + (kg ?? "");
      let row = rows.get(key);
      if (!row) {
        row = { name, meters: log.amount, kilograms: kg, fastest: null, topSpeed: null, last: w.date };
        rows.set(key, row);
      }
      if (w.date > row.last) {
        row.last = w.date;
        row.name = name;
      }
      if (time != null && (!row.fastest || time < row.fastest.time || (time === row.fastest.time && w.date < row.fastest.date))) row.fastest = { time, date: w.date };
      if (speed != null && (!row.topSpeed || speed > row.topSpeed.speed || (speed === row.topSpeed.speed && w.date < row.topSpeed.date)))
        row.topSpeed = { speed, date: w.date };
    }
  }
  return [...rows.values()]
    .map((r): RunBest => ({ name: r.name, meters: r.meters, kilograms: r.kilograms, fastest: r.fastest, topSpeed: r.topSpeed }))
    .sort(
      (a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base", numeric: true }) ||
        a.meters - b.meters ||
        (a.kilograms ?? -1) - (b.kilograms ?? -1),
    );
}

/** "20 m", "20 m at 6 kg". */
export function runDistanceText(meters: number, kg: number | null): string {
  return meters + " m" + (kg != null ? " at " + weightNumber(shownWeight(kg, "kg")) + " " + shownUnit("kg") : "");
}

export function speedText(speed: number): string {
  return speedNumber(speed) + " m/s";
}
